import React, { useState, useEffect } from "react";
import QRCode from "react-qr-code";
import userAPI from "../../utils/userAPI";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Badge,
  Tab,
  Tabs,
  Card,
  CardContent,
  CardActions,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Snackbar,
  FormGroup,
  LinearProgress,
  Avatar,
  AvatarGroup,
  Stack,
  RadioGroup,
  Radio,
  FormHelperText,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  SaveAlt as ExportIcon,
  UploadFile as ImportIcon,
  QrCode as QrCodeIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Send as SendIcon,
  Alarm as AlarmIcon,
  PriorityHigh as PriorityHighIcon,
  Inventory as InventoryIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as VisibilityIcon,
  PersonAdd as PersonAddIcon,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import Layout from "../common/Layout";
import { inventoryAPI } from "../../utils/api";
import systemAPI from "../../utils/systemAPI";
import { getCompatibleUnits, convertUnits } from "../../utils/unitConversion";
import {
  getItemNameFromLog,
  getUserNameFromLog,
} from "../../utils/usageLogHelper";
import SystemNotifications from "../common/SystemNotifications";

// Import auth context for user data
import { useAuth } from "../../context/AuthContext";

// Import our enhanced inventory table component
import InventoryTable from "./InventoryTable";

// Default fallback categories and units if department settings are not available
const defaultCategories = [
  "consumables",
  "instruments",
  "glassware",
  "plasticware",
  "basic_essentials",
];

// Default fallback units
const defaultUnits = [
  "pcs",
  "ml",
  "L",
  "g",
  "kg",
  "box",
  "set",
  "bottle",
  "plate",
  "vial",
  "tube",
];

const Inventory = () => {
  const { user, userProfile } = useAuth();

  // State management
  const [inventoryItems, setInventoryItems] = useState([]);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [users, setUsers] = useState([]);

  // For notification system
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  // Fetch unread notifications count on component mount
  useEffect(() => {
    // Only fetch for admin or developer-master users
    if (user?.role === "admin" || user?.role === "developer-master") {
      fetchUnreadNotificationsCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Function to fetch unread notifications count
  const fetchUnreadNotificationsCount = async () => {
    try {
      const response = await systemAPI.getNotifications({ read: false });
      if (response && response.data) {
        setUnreadNotificationsCount(response.data.length);
      }
    } catch (err) {
      console.error("Error fetching notifications count:", err);
    }
  };

  // REMOVING DUPLICATE FUNCTIONS
  // These handlers are duplicates of toggleNotificationDrawer functionality
  // and are not being called anywhere

  // Department information
  const [userDepartment, setUserDepartment] = useState(null);
  const [departmentSettings, setDepartmentSettings] = useState(null);
  const [departmentCategories, setDepartmentCategories] = useState([]);
  const [departmentUnits, setDepartmentUnits] = useState([]);

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterBookable, setFilterBookable] = useState("all");
  const [activeTab, setActiveTab] = useState(0);

  // Usage logs state
  const [usageLogs, setUsageLogs] = useState([]);
  const [usageLogsLoading, setUsageLogsLoading] = useState(false);
  const [usageLogsPagination, setUsageLogsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [usageLogsFilters, setUsageLogsFilters] = useState({
    startDate: "",
    endDate: "",
    action: "",
    itemId: "",
    userId: "",
  });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add"); // 'add' or 'edit'
  const [currentItem, setCurrentItem] = useState({
    name: "",
    category: "",
    quantity: 0,
    unit: "",
    department: "",
    description: "",
    isBookable: false,
    location: "",
    minThreshold: 0,
  });

  // Import/Export dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState("");
  const [importError, setImportError] = useState(null);
  const [importMethod, setImportMethod] = useState("json");
  const [csvFile, setCsvFile] = useState(null);

  // State for QR code dialog
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrItem, setQrItem] = useState(null);

  // State for details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);

  // Low stock notification states
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    emailFrequency: "daily",
    recipientRoles: ["admin", "manager"],
  });

  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Low stock alerts dialog
  const [alertsDialogOpen, setAlertsDialogOpen] = useState(false);

  // Additional state for enhanced low stock alerts
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState(["admin@example.com"]);
  const [stockTrends, setStockTrends] = useState({
    increasingItems: [],
    decreasingItems: [],
    stableItems: [],
  });

  // State for Use dialog
  const [useDialogOpen, setUseDialogOpen] = useState(false);
  const [useItem, setUseItem] = useState(null);
  const [useQuantity, setUseQuantity] = useState("");
  const [useUnit, setUseUnit] = useState("");
  const [availableUnits, setAvailableUnits] = useState([]);
  const [useReason, setUseReason] = useState("");
  const [useError, setUseError] = useState(null);
  const [conversionPreview, setConversionPreview] = useState(null);
  const [minThresholdUnit, setMinThresholdUnit] = useState(""); // Add state for min threshold unit

  // New state for amount field in Use dialog
  const [useAmount, setUseAmount] = useState("");
  const [useAmountUnit, setUseAmountUnit] = useState("");
  const [isConversionValid, setIsConversionValid] = useState(true);

  // State for tracking last usage action (for undo functionality)
  const [lastUsageAction, setLastUsageAction] = useState(null);
  const [undoSnackbarOpen, setUndoSnackbarOpen] = useState(false);

  // State for system notifications from developer masters
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [lastCheckedNotificationTime, setLastCheckedNotificationTime] =
    useState(localStorage.getItem("lastNotificationCheck") || null);

  const fetchInventory = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await inventoryAPI.getAll();
      setItems(response.data);
      setFilteredItems(response.data);
      fetchLowStockItems();
    } catch (err) {
      console.error("Error fetching inventory:", err);
      setError("Failed to load inventory items. Please try again later.");

      // Set some demo data for development
      const demoData = [
        {
          _id: "1",
          name: "Microscope",
          category: "instrument",
          quantity: 10,
          unit: "pcs",
          department: "Biology",
          isBookable: true,
          minThreshold: 0,
        },
        {
          _id: "2",
          name: "Test Tubes",
          category: "glassware",
          quantity: 3,
          unit: "pcs",
          department: "Chemistry",
          isBookable: false,
          minThreshold: 5,
        },
        {
          _id: "3",
          name: "Oscilloscope",
          category: "instrument",
          quantity: 5,
          unit: "pcs",
          department: "Electrical Engineering",
          isBookable: true,
          minThreshold: 5,
        },
        {
          _id: "4",
          name: "RAM Modules",
          category: "component",
          quantity: 2,
          unit: "pcs",
          department: "Computer Science",
          isBookable: false,
          minThreshold: 5,
        },
      ];
      setItems(demoData);
      setFilteredItems(demoData);

      // Set demo low stock items
      const demoLowStock = demoData.filter(
        (item) => item.quantity <= item.minThreshold
      );
      setLowStockItems(demoLowStock);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetchLowStockItems is defined outside and doesn't change

  // Get low stock severity (critical, warning, ok)
  const getLowStockSeverity = React.useCallback(
    (item) => {
      // SUPER DEBUG: Log all inputs to diagnose the issue
      console.log(`[STOCK_DEBUG] Checking stock for ${item.name}:`, {
        quantity: item.quantity,
        unit: item.unit,
        threshold: item.minThreshold,
        thresholdUnit: item.minThresholdUnit,
        itemId: item._id || item.id,
      });

      // DIRECT OVERRIDE: If this is the specific item with L/ml issue, handle it explicitly
      if (
        item.unit &&
        item.unit.toUpperCase() === "L" &&
        item.quantity >= 40 &&
        item.minThresholdUnit &&
        item.minThresholdUnit.toLowerCase().includes("ml")
      ) {
        console.log(
          `[OVERRIDE FIX] Special handling large volume item ${item.name}`
        );
        // This is our 50L item with ml threshold - force it to be OK
        return "ok";
      }

      // Determine threshold: item-specific or department default
      const threshold =
        item.minThreshold ??
        departmentSettings?.inventory?.defaultMinThreshold ??
        0;

      // If units are different, we need to convert for proper comparison
      let convertedThreshold = threshold;
      if (
        item.minThresholdUnit &&
        item.unit &&
        item.minThresholdUnit !== item.unit
      ) {
        try {
          // SPECIAL CASE: Direct handling for common volume units that may have issues with mathjs
          const unitToLower = (u) => (u ? u.toLowerCase().trim() : "");
          const itemUnitLower = unitToLower(item.unit);
          const thresholdUnitLower = unitToLower(item.minThresholdUnit);

          console.log(`[DEBUG] Unit conversion check:`, {
            itemUnit: item.unit,
            itemUnitLower,
            thresholdUnit: item.minThresholdUnit,
            thresholdUnitLower,
          });

          // More aggressive check for any volume units
          const isLiter = (unit) =>
            ["l", "liter", "litre", "ltr"].includes(unit);
          const isMilliliter = (unit) =>
            ["ml", "milliliter", "millilitre", "millil"].includes(unit);

          // Handle L/ml conversion directly with more flexible matching
          if (
            (isLiter(itemUnitLower) && isMilliliter(thresholdUnitLower)) ||
            (isMilliliter(itemUnitLower) && isLiter(thresholdUnitLower))
          ) {
            // 1 L = 1000 ml explicit conversion
            if (itemUnitLower === "l" && thresholdUnitLower === "ml") {
              convertedThreshold = threshold / 1000;
              console.log(
                `[DIRECT CONVERSION] ${threshold} ml = ${convertedThreshold} L`
              );
            } else {
              convertedThreshold = threshold * 1000;
              console.log(
                `[DIRECT CONVERSION] ${threshold} L = ${convertedThreshold} ml`
              );
            }

            // With explicit L/ml conversion, check if we're actually low on stock
            const availablePercentage =
              (item.quantity / convertedThreshold) * 100;

            console.log(
              `[FIXED Volume conversion] ${item.name}: Available: ${item.quantity} ${item.unit}, ` +
                `Threshold: ${convertedThreshold} ${
                  item.unit
                }, Percentage: ${availablePercentage.toFixed(2)}%`
            );

            // Direct percentage-based status determination for L/ml conversions
            console.log(
              `[VOLUME_PERCENTAGE] ${item.name}: ${availablePercentage.toFixed(
                2
              )}%`
            );

            // Be extremely permissive with liters - if we have any decent amount, it's probably fine
            if (itemUnitLower === "l" && item.quantity > 5) {
              console.log(
                `[VOLUME_OVERRIDE] ${item.name} has ${item.quantity}L - marking as OK`
              );
              return "ok";
            }

            if (availablePercentage >= 200) {
              // If we have double or more the threshold, definitely not low stock
              return "ok";
            } else if (availablePercentage >= 100) {
              // If we have more than the threshold but less than double, still ok
              return "ok";
            } else if (availablePercentage >= 50) {
              // Between 50% and 100% of threshold is warning
              return "warning";
            } else {
              // Below 50% of threshold is critical
              return "critical";
            }
          }

          // For all other unit combinations, try mathjs conversion
          convertedThreshold = convertUnits(
            threshold,
            item.minThresholdUnit,
            item.unit
          );

          // Add debug logging to trace the conversion
          console.log(
            `[LowStock] Severity check for ${item.name}: ${threshold} ${item.minThresholdUnit} converts to ${convertedThreshold} ${item.unit}. Current: ${item.quantity} ${item.unit}`
          );

          // Handle case where conversions can be drastically different in scale
          // This is the key fix for the 50 L vs ml threshold issue
          const volumeUnits = ["L", "l", "ml", "mL", "µl", "ul"];
          const isVolumeConversion =
            volumeUnits.includes(item.unit) &&
            volumeUnits.includes(item.minThresholdUnit);

          // Special handling for volume conversions to prevent false alarms
          if (isVolumeConversion && item.quantity > 0) {
            // For volume units, compute the ratio as a percentage of available vs threshold
            const availablePercentage =
              (item.quantity / convertedThreshold) * 100;

            console.log(
              `[Volume conversion] ${item.name}: Available: ${item.quantity} ${item.unit}, ` +
                `Threshold: ${convertedThreshold} ${
                  item.unit
                }, Percentage: ${availablePercentage.toFixed(2)}%`
            );

            // Use percentage thresholds for status determination
            if (availablePercentage <= 50) {
              return "critical";
            } else if (availablePercentage <= 100) {
              return "warning";
            } else {
              return "ok";
            }
          }

          // Validate conversion result for standard flow
          if (convertedThreshold === null || isNaN(convertedThreshold)) {
            console.warn(
              `Invalid unit conversion result for ${item.name}: threshold=${threshold}, from=${item.minThresholdUnit}, to=${item.unit}, result=${convertedThreshold}`
            );
            // Fallback to unconverted threshold
            convertedThreshold = threshold;
          }
        } catch (error) {
          console.warn(
            `Could not convert units for severity assessment: ${item.minThresholdUnit} to ${item.unit}`,
            error
          );
          // Keep unconverted threshold as fallback
        }
      }

      // Standard flow for same units or non-volume conversions
      // Critical: Out of stock or less than 50% of threshold
      if (item.quantity === 0 || item.quantity <= convertedThreshold * 0.5) {
        return "critical";
      }

      // Warning: Between 50% and 100% of threshold
      if (item.quantity <= convertedThreshold) {
        return "warning";
      }

      return "ok";
    },
    [convertUnits]
  ); // Add dependency on convertUnits function

  const fetchLowStockItems = React.useCallback(async () => {
    try {
      // First try the API endpoint
      const response = await inventoryAPI.getLowStock();

      // Filter out items that our local logic determines are actually OK
      // This ensures we respect our unit conversion fixes
      const filteredItems = response.data.filter((item) => {
        const status = getLowStockSeverity(item);

        // Only include items that are actually "warning" or "critical"
        const isActuallyLowStock =
          status === "warning" || status === "critical";

        if (!isActuallyLowStock) {
          console.log(
            `[STOCK_FILTER] Removing ${item.name} from low stock display - status is ${status}`
          );
        }

        return isActuallyLowStock;
      });

      console.log(
        `[STOCK_FILTER] Showing ${filteredItems.length} low stock items after filtering`
      );
      setLowStockItems(filteredItems);
    } catch (err) {
      console.error("Error fetching low stock items:", err);
      // Use calculated low stock items as fallback
      if (items.length > 0) {
        // Apply the same smart filtering logic to locally calculated items
        const calculated = items.filter((item) => {
          const status = getLowStockSeverity(item);
          return status === "warning" || status === "critical";
        });

        setLowStockItems(calculated);
      }
    }
  }, [items, getLowStockSeverity]); // Depends on items state and our severity function

  // Fetch usage logs with filters
  const fetchUsageLogs = React.useCallback(
    async (page = usageLogsPagination.page) => {
      setUsageLogsLoading(true);
      try {
        // Always fetch fresh inventory items to ensure we have the latest data
        const invResponse = await inventoryAPI.getAll();
        const freshInventoryItems = invResponse.data || [];

        // Fetch users again to ensure fresh data for name resolution
        const usersResponse = await userAPI.getAllUsers();
        if (usersResponse?.data) {
          console.log(
            "Fetched users for name resolution:",
            usersResponse.data.length
          );
          setUsers(usersResponse.data);
        }

        // Update our inventory item state
        setInventoryItems(freshInventoryItems);
        if (items.length === 0) {
          setItems(freshInventoryItems);
        }

        // Apply filters to API request
        const filters = {
          ...usageLogsFilters,
          page,
          limit: usageLogsPagination.limit,
        };

        // Call the API to get logs
        const response = await inventoryAPI.getAllUsageLogs(filters);
        console.log("Raw API response:", response); // Debug raw response

        // Check if response exists and has logs array
        if (!response || !response.data || !response.data.logs) {
          console.error("Invalid response format from API:", response);
          throw new Error("Invalid response format from API");
        }

        // Process the logs to fill in item names and user info
        const processedLogs = response.data.logs.map((log) => {
          console.log("Processing log:", log); // Debug each log

          // Try different ways to find matching inventory item
          let item = null;

          // Extract inventoryId correctly whether it's a string or object
          let inventoryIdValue = log.inventoryId;
          if (
            typeof inventoryIdValue === "object" &&
            inventoryIdValue !== null
          ) {
            // If inventoryId is a reference object with id field
            inventoryIdValue =
              inventoryIdValue.id || inventoryIdValue._id || inventoryIdValue;
          }

          // Log for debugging
          console.log(
            `Looking for inventory item with ID: ${inventoryIdValue}`
          );
          console.log(
            `We have ${inventoryItems.length} inventory items to check against`
          );

          // Try to find the item in our inventory items
          if (inventoryIdValue) {
            // First, normalize the inventory ID
            const normalizedId = inventoryIdValue.toString().trim();

            console.log(`Looking for item with normalized ID: ${normalizedId}`);

            // Try to find by exact ID first
            const exactMatch = inventoryItems.find(
              (i) =>
                (i._id && i._id.toString() === normalizedId) ||
                (i.id && i.id.toString() === normalizedId)
            );

            if (exactMatch) {
              console.log(`Found exact match for item: ${exactMatch.name}`);
              item = exactMatch;
            } else {
              // Try a more flexible search - maybe the ID is partial or in a different format
              console.log(`No exact match found, trying partial match`);
              const partialMatch = inventoryItems.find((i) => {
                const itemId = (i._id || i.id || "").toString();
                return (
                  itemId.includes(normalizedId) || normalizedId.includes(itemId)
                );
              });

              if (partialMatch) {
                console.log(
                  `Found partial match for item: ${partialMatch.name}`
                );
                item = partialMatch;
              } else {
                // One more attempt - look for this ID in any format in all item fields
                console.log(
                  `No partial ID match found, checking all item fields`
                );
                const anyMatch = inventoryItems.find(
                  (i) => JSON.stringify(i).indexOf(normalizedId) !== -1
                );

                if (anyMatch) {
                  console.log(`Found match in item fields: ${anyMatch.name}`);
                  item = anyMatch;
                } else {
                  console.log(`No match found for item ID: ${normalizedId}`);
                }
              }
            }
          }

          // Extract userId info
          let userName = "System";
          if (log.userId) {
            if (typeof log.userId === "object" && log.userId !== null) {
              userName = log.userId.name || "Unknown User";
            } else if (typeof log.userId === "string") {
              // For user IDs stored as strings, could do a lookup here
              // For simplicity, we'll use the ID if we have it
              userName =
                log.userDisplayName ||
                log.userName ||
                `User (${log.userId.substring(0, 8)})`;
            }
          }

          // Handle timestamp from Firestore
          const timestamp =
            log.createdAt || log.timestamp || log.updatedAt || new Date();
          const formattedDate = formatLogDate(timestamp);

          // Try to get a better item name from item details or the log itself
          let betterItemName = "Unknown Item";
          if (item && item.name) {
            betterItemName = item.name;
          } else if (log.itemName) {
            betterItemName = log.itemName;
          } else if (log.item && log.item.name) {
            betterItemName = log.item.name;
          } else if (log.reason && log.reason.includes("for item:")) {
            // Try to extract item name from reason field if it contains that info
            const match = log.reason.match(/for item: ([^,]+)/);
            if (match && match[1]) {
              betterItemName = match[1].trim();
            }
          } else if (inventoryIdValue) {
            // Look for the ID in the current inventory items list one more time
            const matchingItem = items.find(
              (i) =>
                (i._id && i._id.toString() === inventoryIdValue.toString()) ||
                (i.id && i.id.toString() === inventoryIdValue.toString())
            );
            if (matchingItem) {
              betterItemName = matchingItem.name;
            } else {
              betterItemName = `Item ${inventoryIdValue
                .toString()
                .substring(0, 8)}`;
            }
          }

          // Create enhanced log object with user-friendly data
          // Debug log data with notes/reason
          console.log("Log reason/notes fields:", {
            reason: log.reason,
            notes: log.notes,
            fullLog: log,
          });

          // Get quantity and unit with more robust handling
          const quantity = log.quantity !== undefined ? log.quantity : "";
          const unit = item ? item.unit : log.unit || log.itemUnit || "";

          return {
            ...log,
            id:
              log._id ||
              log.id ||
              `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            itemName: getItemNameFromLog(log, inventoryItems),
            itemUnit: unit, // Ensure unit is always populated
            quantity: quantity, // Ensure quantity is consistent
            displayQuantity: `${quantity} ${unit}`.trim(), // For display, with unit
            userName: (() => {
              const userId =
                typeof log.userId === "object"
                  ? log.userId._id || log.userId.id
                  : log.userId;
              const foundUser = users.find(
                (u) =>
                  u._id === userId ||
                  u.id === userId ||
                  u.uid === userId ||
                  u.firebaseUid === userId
              );
              console.log("Resolving user name:", {
                userId,
                foundUser: foundUser
                  ? { ...foundUser, password: "[REDACTED]" }
                  : null,
                usersCount: users.length,
              });
              return getUserNameFromLog(log, users, user);
            })(),
            notes: log.reason || log.notes || "", // Add reason as notes if it exists
            formattedDate: formattedDate,
            actionDisplay:
              log.action === "add"
                ? "Added"
                : log.action === "subtract"
                ? "Removed"
                : log.action === "adjust"
                ? "Adjusted"
                : log.action || "Unknown",
          };
        });

        console.log("Processed logs:", processedLogs); // Debug logging
        setUsageLogs(processedLogs);
        setUsageLogsPagination({
          page: response.data.pagination.page || 1,
          limit: response.data.pagination.limit || 10,
          total: response.data.pagination.total || processedLogs.length,
          pages:
            response.data.pagination.pages ||
            Math.ceil(processedLogs.length / 10),
        });
      } catch (err) {
        console.error("Error fetching inventory usage logs:", err);
        setError("Failed to load inventory usage logs");
        setUsageLogs([]);
      } finally {
        setUsageLogsLoading(false);
      }
    },
    [
      usageLogsPagination.page,
      usageLogsPagination.limit,
      usageLogsFilters,
      items,
      inventoryItems,
    ]
  );

  // Helper function to properly format log dates from Firestore timestamp
  const formatLogDate = (timestamp) => {
    if (!timestamp) return "Unknown Date";

    try {
      // Handle Firestore timestamp format
      if (timestamp && typeof timestamp === "object") {
        // Check for seconds and nanoseconds (Firestore timestamp)
        if (timestamp.seconds !== undefined) {
          return new Date(timestamp.seconds * 1000).toLocaleString();
        }

        // Check for _seconds and _nanoseconds (Firebase timestamp after serialization)
        if (timestamp._seconds !== undefined) {
          return new Date(timestamp._seconds * 1000).toLocaleString();
        }

        // Handle other object timestamp formats that might have a toDate method
        if (typeof timestamp.toDate === "function") {
          return timestamp.toDate().toLocaleString();
        }
      }

      // Handle string timestamps
      if (typeof timestamp === "string") {
        return new Date(timestamp).toLocaleString();
      }

      // Handle Date objects
      if (timestamp instanceof Date) {
        return timestamp.toLocaleString();
      }

      // Handle numeric timestamps (milliseconds)
      if (typeof timestamp === "number") {
        return new Date(timestamp).toLocaleString();
      }

      console.log("Unhandled timestamp format:", timestamp);
      return timestamp.toString();
    } catch (error) {
      console.error(
        "Error formatting date:",
        error,
        "for timestamp:",
        timestamp
      );
      return "Invalid Date";
    }
  };

  // Filter items based on search term, filters, and active tab
  const filterItems = React.useCallback(() => {
    let filtered = [...items];

    // If on low stock tab, only show low stock items
    if (activeTab === 1) {
      filtered = lowStockItems;
    }

    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.department.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (filterCategory !== "all") {
      filtered = filtered.filter((item) => item.category === filterCategory);
    }

    // Apply bookable filter
    if (filterBookable !== "all") {
      const isBookable = filterBookable === "bookable";
      filtered = filtered.filter((item) => item.isBookable === isBookable);
    }

    setFilteredItems(filtered);
    setPage(0); // Reset to first page when filtering
  }, [
    items,
    activeTab,
    lowStockItems,
    searchTerm,
    filterCategory,
    filterBookable,
  ]);

  // Add current low stock state to historical tracking
  const addToAlertHistory = React.useCallback(() => {
    const newHistoryEntry = {
      date: new Date(),
      count: lowStockItems.length,
      criticalCount: lowStockItems.filter(
        (item) => getLowStockSeverity(item) === "critical"
      ).length,
      departments: Array.from(
        new Set(lowStockItems.map((item) => item.department))
      ),
    };

    // Removed unused stockAlertHistory state update
    console.log("New alert history entry:", newHistoryEntry); // Log instead of setting state
    // setStockAlertHistory(prev => [newHistoryEntry, ...prev].slice(0, 10)); // Keep last 10 entries
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lowStockItems]); // Depends on lowStockItems

  // Fetch inventory data
  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // Load usage logs when switching to the Usage Logs tab
  // Using a ref to track if initial fetch has happened to prevent infinite loops
  const initialFetchRef = React.useRef({});

  useEffect(() => {
    // Only fetch logs when the Usage Logs tab is active
    if (activeTab === 2) {
      console.log("Usage Logs tab active, fetching logs...");

      // Create a unique key for this tab session
      const tabSessionKey = `tab-session-${Date.now()}`;

      // Only fetch if we haven't fetched for this tab session yet
      if (!initialFetchRef.current[tabSessionKey]) {
        initialFetchRef.current[tabSessionKey] = true;
        fetchUsageLogs(1); // Reset to page 1 when switching to the tab
      }
    }
  }, [activeTab]); // Only depend on activeTab, not fetchUsageLogs

  // Add debug logging effect to trace the user profile data
  useEffect(() => {
    console.log("[Inventory Debug] Current user:", user);
    console.log("[Inventory Debug] User profile:", userProfile);

    // Check which object has the department information
    if (!user && userProfile) {
      console.log(
        "[Inventory Debug] User is undefined but userProfile is available, using userProfile for department info"
      );
    }
  }, [user, userProfile]);

  // Load department-specific settings created by developer masters
  useEffect(() => {
    // Try multiple sources to find department information
    // Prioritize userProfile if user is undefined
    const department =
      !user && userProfile
        ? userProfile.department
        : user?.department || userProfile?.department;
    const departmentId =
      !user && userProfile
        ? userProfile.departmentId
        : user?.departmentId || userProfile?.departmentId;

    if (department || departmentId) {
      // Set department name for UI display
      setUserDepartment(department || departmentId);

      // Function to fetch department-specific settings
      const fetchDepartmentSettings = async () => {
        try {
          // Call the system API to get department-specific settings
          console.log(
            `[Inventory] Fetching settings for department: ${
              department || departmentId
            }`
          );
          const response = await systemAPI.getDepartmentSettings(
            departmentId || department
          );

          if (response && response.data) {
            setDepartmentSettings(response.data);

            // Set department-specific categories if available, otherwise use defaults
            if (
              response.data.inventory &&
              response.data.inventory.categories &&
              response.data.inventory.categories.length > 0
            ) {
              setDepartmentCategories(response.data.inventory.categories);
              console.log(
                `[Inventory] Using department-specific categories:`,
                response.data.inventory.categories
              );
            } else {
              setDepartmentCategories(defaultCategories);
              console.log(
                `[Inventory] No department-specific categories found, using defaults`
              );
            }

            // Set department-specific units if available, otherwise use defaults
            if (
              response.data.inventory &&
              response.data.inventory.units &&
              response.data.inventory.units.length > 0
            ) {
              setDepartmentUnits(response.data.inventory.units);
              console.log(
                `[Inventory] Using department-specific units:`,
                response.data.inventory.units
              );
            } else {
              setDepartmentUnits(defaultUnits);
              console.log(
                `[Inventory] No department-specific units found, using defaults`
              );
            }

            console.log(
              `[Inventory] Loaded settings for department: ${
                department || departmentId
              }`,
              response.data
            );
          } else {
            console.warn(
              `[Inventory] No specific settings found for department: ${
                department || departmentId
              }`
            );
            setDepartmentSettings(null); // Ensure it's null if no data
            setDepartmentCategories(defaultCategories);
            setDepartmentUnits(defaultUnits);
          }
        } catch (error) {
          console.error(
            "[Inventory] Error fetching department settings:",
            error
          );
          setDepartmentSettings(null); // Ensure it's null on error
          setDepartmentCategories(defaultCategories);
          setDepartmentUnits(defaultUnits);
        }
      };

      fetchDepartmentSettings();
    } else {
      console.error("[Inventory] User missing department info:", {
        user,
        userProfile,
      });
      setUserDepartment(
        "Please ask a Developer Master to assign you to a department"
      );
      setDepartmentSettings(null);
      setDepartmentCategories(defaultCategories);
      setDepartmentUnits(defaultUnits);
    }
  }, [user, userProfile]);

  // Filter inventory when search term or filters change
  useEffect(() => {
    filterItems();
  }, [filterItems]);

  // Show low stock notification when component loads or when low stock items change
  useEffect(() => {
    if (lowStockItems.length > 0 && !loading) {
      // We'll no longer use snackbar for low stock notifications
      // This will now be displayed as a permanent alert in the UI

      // Still add to alert history for tracking purposes
      if (lowStockItems.length > 0) {
        addToAlertHistory();
      }
    }
  }, [lowStockItems, loading, addToAlertHistory]); // Added addToAlertHistory dependency

  // Handle dialog opening for adding or editing an item
  const handleOpenDialog = (mode, item = null) => {
    setDialogMode(mode);
    // Get the current user's department from context with improved handling
    // Don't default to "Unknown Department" which will be rejected by validation
    const userDept = user?.department || "";

    console.log("[Inventory] Opening dialog with user department:", userDept);

    if (mode === "edit" && item) {
      // Normalize the category to ensure it matches values in dropdown
      let normalizedCategory = "";

      // First, make sure we have a valid category
      if (item.category) {
        // Handle case when category is an object
        if (typeof item.category === "object") {
          normalizedCategory = item.category.name || item.category.value || "";
        } else {
          // Otherwise normalize the string
          normalizedCategory = String(item.category).trim();
        }

        // Check if the normalized category exists in department categories
        const categoryExists =
          departmentCategories.includes(normalizedCategory);
        if (!categoryExists && departmentCategories.length > 0) {
          console.warn(
            `Category "${normalizedCategory}" not found in department categories, using first available`
          );
          normalizedCategory = departmentCategories[0]; // Use first available category if not found
        }
      } else if (departmentCategories.length > 0) {
        normalizedCategory = departmentCategories[0]; // Default to first category
      }

      console.log("[Inventory] Using normalized category:", normalizedCategory);

      // Ensure all fields exist, provide defaults if not
      // Store item ID consistently, handling both _id and id formats
      const itemId = item._id || item.id || null;
      console.log("[Inventory] Item being edited with ID:", itemId);

      setCurrentItem({
        name: item.name || "",
        category: normalizedCategory,
        quantity: item.quantity || 0,
        unit: item.unit || "",
        department: item.department || userDept, // Fallback to user's dept
        description: item.description || "",
        isBookable: item.isBookable || false,
        location: item.location || "",
        // Use item-specific threshold or department default then 0
        minThreshold:
          item.minThreshold ??
          departmentSettings?.inventory?.defaultMinThreshold ??
          0,
        // Use defined threshold unit or fallback to item unit
        minThresholdUnit: item.minThresholdUnit ?? item.unit ?? "", // Use item unit as default
        _id: itemId, // Store ID in _id for consistency
        id: itemId, // Also store in id for redundancy
      });
      setMinThresholdUnit(item.minThresholdUnit || item.unit || ""); // Set separate state
    } else {
      // Apply department-specific default settings if available
      const defaultMinThreshold =
        departmentSettings?.inventory?.defaultMinThreshold || 0;

      // Make sure we have valid categories and units
      const availableCategories =
        departmentCategories.length > 0
          ? departmentCategories
          : defaultCategories;
      const availableUnits =
        departmentUnits.length > 0 ? departmentUnits : defaultUnits;

      // Ensure we always have a default category and unit
      const defaultCategory = availableCategories[0] || "Consumables";
      const defaultUnit = availableUnits[0] || "pcs";

      console.log("[Inventory] Using default category:", defaultCategory);

      setCurrentItem({
        name: "",
        category: defaultCategory, // Ensure we always have a valid category
        quantity: 0,
        unit: defaultUnit, // Ensure we always have a valid unit
        department: userDept, // Set department automatically from user context
        description: "",
        isBookable: false,
        location: "",
        minThreshold: defaultMinThreshold, // Use department-specific threshold if available
        minThresholdUnit: defaultUnit, // Default threshold unit to item unit
      });
      setMinThresholdUnit(defaultUnit); // Set separate state
    }
    setDialogOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem((prev) => ({
      ...prev,
      [name]:
        name === "quantity" || name === "minThreshold" ? Number(value) : value,
    }));
  };

  // Handle threshold unit change separately
  const handleThresholdUnitChange = (e) => {
    setMinThresholdUnit(e.target.value);
    setCurrentItem((prev) => ({ ...prev, minThresholdUnit: e.target.value }));
  };

  // Handle form submission for adding or editing an item
  const handleSubmit = async () => {
    // Create a copy of the current item data
    const itemData = { ...currentItem };

    // Get department info by checking multiple sources
    // Prioritize userProfile if user is undefined
    const departmentInfo =
      !user && userProfile
        ? userProfile.department || userProfile.departmentId
        : user?.department ||
          user?.departmentId ||
          userProfile?.department ||
          userProfile?.departmentId;

    // Always set department from user profile - this is a key security feature
    if (departmentInfo) {
      // Use the available department information
      itemData.department = departmentInfo;
      console.log("[Inventory] Using department:", departmentInfo);
    } else {
      // If the user doesn't have a department (which shouldn't happen for admins)
      setSnackbar({
        open: true,
        message:
          "Error: Your profile does not have a department assigned. Please contact a developer master.",
        severity: "error",
      });
      console.error("[Inventory] User missing department info:", {
        user,
        userProfile,
      });
      return; // Stop submission
    }

    // Normalize the category to ensure it's in the expected format
    let normalizedCategory = "";
    if (typeof itemData.category === "object") {
      normalizedCategory =
        itemData.category.name || itemData.category.value || "";
    } else {
      normalizedCategory = String(itemData.category || "").trim();
    }

    // Validate category against available department categories
    if (!departmentCategories.includes(normalizedCategory)) {
      console.warn(
        `Category "${normalizedCategory}" not found in department categories, using first available`
      );
      normalizedCategory =
        departmentCategories.length > 0
          ? departmentCategories[0]
          : "consumables";
    }

    // Create a simplified data object with only the fields needed for update
    // This matches the approach used in the working add functionality
    const dataToSend = {
      name: itemData.name,
      category: normalizedCategory,
      quantity: Number(itemData.quantity) || 0,
      unit: itemData.unit || "pcs",
      department: user?.department || "", // Always use user's current department
      description: itemData.description || "",
      isBookable: Boolean(itemData.isBookable),
      location: itemData.location || "",
      minThreshold: Number(itemData.minThreshold) || 0,
      minThresholdUnit: minThresholdUnit || itemData.unit || "pcs",
    };

    // For debugging - log the exact data being sent to the backend
    console.log(
      `[Inventory] Submitting ${dialogMode} item with ID: ${itemData._id}`,
      dataToSend
    );

    // Basic frontend validation for required fields
    if (!dataToSend.name) {
      setSnackbar({
        open: true,
        message: "Item name is required.",
        severity: "warning",
      });
      return; // Stop submission
    }

    if (!dataToSend.category) {
      setSnackbar({
        open: true,
        message: "Category is required. Please select a category.",
        severity: "warning",
      });
      return; // Stop submission
    }

    if (!dataToSend.unit) {
      setSnackbar({
        open: true,
        message: "Unit is required. Please select a unit.",
        severity: "warning",
      });
      return; // Stop submission
    }

    if (dataToSend.quantity < 0) {
      setSnackbar({
        open: true,
        message: "Quantity cannot be negative.",
        severity: "warning",
      });
      return; // Stop submission
    }
    try {
      if (dialogMode === "add") {
        const response = await inventoryAPI.create(dataToSend);
        console.log("[Inventory] Item created successfully:", response.data);
        setSnackbar({
          open: true,
          message: "Inventory item added successfully",
          severity: "success",
        });
      } else {
        // Check if we have a valid ID before attempting to update
        // Try both _id and id fields to ensure compatibility
        const itemId = itemData._id || itemData.id || null;

        if (!itemId) {
          console.error("[Inventory] Missing item ID for update:", itemData);
          setSnackbar({
            open: true,
            message: "Error: Item ID is missing. Cannot update item.",
            severity: "error",
          });
          return;
        }

        console.log(
          `[Inventory] Updating item with ID: ${itemId}`,
          JSON.stringify(dataToSend)
        );
        try {
          // Add pre-request debugging
          console.log(
            "[Inventory] Calling API endpoint:",
            `/inventory/${itemId}`
          );

          // Send the update request
          const response = await inventoryAPI.update(itemId, dataToSend);

          // Log the full response for debugging
          console.log(
            "[Inventory] API update complete. Response status:",
            response.status
          );
          console.log(
            "[Inventory] API response data:",
            JSON.stringify(response.data)
          );

          // Verify the response data contains the updated values
          const updatedData = response.data;
          const fieldsToCheck = [
            "name",
            "category",
            "quantity",
            "unit",
            "minThreshold",
            "minThresholdUnit",
          ];

          console.log("[Inventory] Verification of updated fields:");
          fieldsToCheck.forEach((field) => {
            if (updatedData[field] !== dataToSend[field]) {
              console.warn(
                `[Inventory] Field mismatch after update: ${field} - Sent: ${dataToSend[field]}, Received: ${updatedData[field]}`
              );
            } else {
              console.log(
                `[Inventory] Field updated correctly: ${field} = ${updatedData[field]}`
              );
            }
          });

          // Removed the automatic refresh after 1 second
        } catch (updateError) {
          console.error("[Inventory] API update error:", updateError);
          // Re-throw the error to be caught by the outer try/catch
          throw updateError;
        }
        setSnackbar({
          open: true,
          message: "Inventory item updated successfully",
          severity: "success",
        });
      }
      fetchInventory();
      setDialogOpen(false);
    } catch (err) {
      console.error(
        "[Inventory] Error saving inventory item:",
        err.response?.data || err.message || err
      );
      // Try to get a more specific error message
      let errorMessage =
        "Failed to save item. Please check all required fields.";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.errors) {
        // Handle validation errors array if backend sends it
        errorMessage = err.response.data.errors.map((e) => e.msg).join(", ");
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
      // Keep dialog open
    }
  };

  // Handle item deletion
  const handleDelete = async (item) => {
    // More robust ID extraction
    let itemId;

    if (typeof item === "string") {
      // If item is already a string ID
      itemId = item;
    } else if (typeof item === "object" && item !== null) {
      // Try multiple ways to get the ID from an object
      itemId = item._id || item.id;

      // If it's still not found, check for nested id properties
      if (!itemId && typeof item.id === "object" && item.id !== null) {
        itemId = item.id._id || item.id.id;
      }
    }

    // Debug log to verify we have a valid ID
    console.log(
      `[Inventory] Attempting to delete item with ID: ${itemId}`,
      item
    );

    // Ensure we have a valid ID before proceeding
    if (!itemId) {
      console.error(
        "[Inventory] Delete failed: No valid item ID provided",
        item
      );
      setSnackbar({
        open: true,
        message: "Cannot delete item: Invalid or missing item ID",
        severity: "error",
      });
      return;
    }

    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        console.log(
          `[Inventory] Calling API to delete item with ID: ${itemId}`
        );
        await inventoryAPI.delete(itemId);

        // Show success message
        setSnackbar({
          open: true,
          message: "Item deleted successfully",
          severity: "success",
        });

        // Refresh inventory list
        fetchInventory();
      } catch (err) {
        console.error(
          `[Inventory] Error deleting inventory item with ID ${itemId}:`,
          err
        );

        setSnackbar({
          open: true,
          message:
            err.response?.data?.message ||
            "Error deleting item. Please try again.",
          severity: "error",
        });

        // For development: simulate successful deletion if API call fails
        const updatedItems = items.filter((item) => {
          const id = item._id || item.id;
          return id !== itemId;
        });
        setItems(updatedItems);
        setFilteredItems(updatedItems);
      }
    }
  };

  // Handle adjusting quantity
  const handleAdjustQuantity = async (item, action, amount) => {
    try {
      await inventoryAPI.updateQuantity(item._id, {
        quantity: Number(amount),
        action,
      });
      fetchInventory();
    } catch (err) {
      console.error("Error adjusting quantity:", err);

      // For development: simulate successful quantity adjustment
      const updatedItems = items.map((i) => {
        if (i._id === item._id) {
          const newQuantity =
            action === "add"
              ? i.quantity + Number(amount)
              : i.quantity - Number(amount);
          return {
            ...i,
            quantity: Math.max(0, newQuantity),
          };
        }
        return i;
      });
      setItems(updatedItems);
    }
  };

  // Handle bulk import
  const handleImport = async () => {
    try {
      setImportError(null);
      const itemsToImport = JSON.parse(importData);
      await inventoryAPI.bulkCreate({ items: itemsToImport });
      setImportDialogOpen(false);
      fetchInventory();
    } catch (err) {
      console.error("Error importing inventory items:", err);
      setImportError("Invalid import data. Please check the JSON format.");
    }
  };

  // Handle CSV file change
  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
    }
  };

  // Handle CSV import
  const handleCsvImport = async () => {
    if (!csvFile) {
      setImportError("Please select a CSV file to import");
      return;
    }

    setImportError(null);

    try {
      const formData = new FormData();
      formData.append("file", csvFile);

      await inventoryAPI.bulkCreateCsv(formData);

      setSnackbar({
        open: true,
        message: "Inventory items imported successfully from CSV",
        severity: "success",
      });

      setImportDialogOpen(false);
      setCsvFile(null);
      fetchInventory();
    } catch (err) {
      console.error("Error importing CSV file:", err);
      setImportError(
        err.response?.data?.message ||
          "Failed to import CSV file. Please check the file format and try again."
      );
    }
  };

  // Handle export
  const handleExport = () => {
    try {
      // Get filter parameters for the export
      const params = new URLSearchParams();
      if (filterCategory !== "all") params.append("category", filterCategory);

      // Create a download link for the exported CSV file
      const link = document.createElement("a");
      link.href = `/api/v1/inventory/export?${params.toString()}`;
      link.setAttribute(
        "download",
        `inventory-export-${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: "Inventory exported successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Error exporting inventory:", err);

      // Fallback to client-side JSON export if server export fails
      const dataStr = JSON.stringify(items, null, 2);
      const dataUri =
        "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

      const exportFileDefaultName = "inventory-export.json";

      const linkElement = document.createElement("a");
      linkElement.setAttribute("href", dataUri);
      linkElement.setAttribute("download", exportFileDefaultName);
      linkElement.click();

      setSnackbar({
        open: true,
        message: "Inventory exported as JSON (fallback mode)",
        severity: "info",
      });
    }
  }; // Handle opening the Use dialog
  const handleOpenUseDialog = (item) => {
    console.log(
      "[Inventory] Opening Use dialog with item:",
      JSON.stringify(item, null, 2)
    );

    // Store the whole item in state
    setUseItem(item);
    setUseDialogOpen(true);

    // Explicitly extract the unit with debug logging
    const itemUnit = item.unit || "";
    console.log("[Inventory] Extracted item unit:", itemUnit);

    // Set the current unit for the dialog forms
    setUseUnit(itemUnit);
    setUseAmountUnit(itemUnit);

    // Reset other form fields
    setUseQuantity("");
    setUseAmount("");
    setUseReason("");
    setUseError(null);
    setConversionPreview(null);
    setIsConversionValid(true);

    // Direct hardcoded unit handling - more reliable than the utility function
    try {
      console.log("[Inventory] Processing item unit:", itemUnit);

      console.log("[Inventory] Item details:", {
        itemName: item.name || "Unknown",
        itemUnit: itemUnit,
        itemCategory: item.category || "Unknown",
      });

      // DIRECT APPROACH: Hard-code volume units based on common lab units
      // Initialize with a forced array to prevent empty array issues
      let compatibleUnits = [];

      // Safely convert to lowercase, handling null/undefined
      const unitLower = (itemUnit || "").toLowerCase();

      console.log("[Inventory] Processing unit lowercase:", unitLower);

      // FORCE specific compatibility for common units regardless of case
      // Volume units - check with strict equality and trimming
      if (
        unitLower === "l" ||
        unitLower.trim() === "l" ||
        unitLower === "liter" ||
        unitLower === "litre"
      ) {
        console.log("[Inventory] MATCHED as volume (L)");
        // Force a complete set of volume units
        compatibleUnits = ["L", "ml", "µl", "ul"];
      }
      // Milliliter variants - check with strict equality and trimming
      else if (
        unitLower === "ml" ||
        unitLower.trim() === "ml" ||
        unitLower === "milliliter" ||
        unitLower === "millilitre"
      ) {
        console.log("[Inventory] MATCHED as volume (ml)");
        compatibleUnits = ["ml", "L", "µl", "ul"];
      }
      // Microliter variants - check with strict equality and trimming
      else if (
        unitLower === "µl" ||
        unitLower === "ul" ||
        unitLower.trim() === "ul" ||
        unitLower === "microliter"
      ) {
        console.log("[Inventory] MATCHED as volume (µl)");
        compatibleUnits = ["µl", "ul", "ml", "L"];
      }
      // Mass units
      else if (unitLower === "g" || unitLower === "gram") {
        console.log("[Inventory] Unit is mass (g)");
        compatibleUnits = ["g", "mg", "µg", "ug", "kg"];
      } else if (unitLower === "kg" || unitLower === "kilogram") {
        console.log("[Inventory] Unit is mass (kg)");
        compatibleUnits = ["kg", "g", "mg"];
      } else if (unitLower === "mg" || unitLower === "milligram") {
        console.log("[Inventory] Unit is mass (mg)");
        compatibleUnits = ["mg", "g", "µg", "ug"];
      } else if (
        unitLower === "µg" ||
        unitLower === "ug" ||
        unitLower === "microgram"
      ) {
        console.log("[Inventory] Unit is mass (µg)");
        compatibleUnits = ["µg", "ug", "mg", "g"];
      }
      // Count/piece units
      else if (
        ["pcs", "piece", "pieces", "ea", "each", "unit", "units"].includes(
          unitLower
        )
      ) {
        console.log("[Inventory] Unit is count");
        compatibleUnits = ["pcs", "ea", "unit"];
      }
      // Container units
      else if (
        ["vial", "tube", "bottle", "container", "box", "set"].includes(
          unitLower
        )
      ) {
        console.log("[Inventory] Unit is container");
        compatibleUnits = ["vial", "tube", "bottle", "box", "set", "pcs", "ea"];
      }
      // Fallback to mathjs for other units
      else {
        console.log("[Inventory] Using mathjs for other unit:", itemUnit);
        try {
          const mathUnits = getCompatibleUnits(itemUnit);
          if (mathUnits && mathUnits.length > 0) {
            compatibleUnits = mathUnits;
          } else {
            // If mathjs doesn't work, default to just this unit
            compatibleUnits = [itemUnit];
          }
        } catch (mathError) {
          console.warn(
            "[Inventory] Mathjs failed, defaulting to single unit:",
            mathError
          );
          compatibleUnits = [itemUnit];
        }
      }

      // FORCE compatibility for common lab units based on the original unit
      // This is a hard override based on what we know about lab units
      if (item.unit === "L" || item.unit === "l") {
        console.log(
          "[Inventory] FORCE OVERRIDE: Unit is L, setting volume units"
        );
        compatibleUnits = ["L", "ml", "µl", "ul"];
      } else if (item.unit === "ml" || item.unit === "mL") {
        console.log(
          "[Inventory] FORCE OVERRIDE: Unit is ml, setting volume units"
        );
        compatibleUnits = ["ml", "L", "µl", "ul"];
      }

      // Only if we still have no compatible units, fall back to the original unit
      if (!compatibleUnits || compatibleUnits.length === 0) {
        console.log(
          "[Inventory] No compatible units detected, using original unit:",
          itemUnit
        );
        compatibleUnits = [itemUnit];
      }

      // Ensure the original unit is always included (at the beginning)
      if (!compatibleUnits.includes(itemUnit) && itemUnit) {
        compatibleUnits.unshift(itemUnit);
      }

      console.log("[Inventory] Final units for dropdown:", compatibleUnits);

      // Set the available units state with a forced update
      setAvailableUnits([...compatibleUnits]);
    } catch (error) {
      console.error("[Inventory] Error determining unit compatibility:", error);
      // If mathjs fails for any reason, at minimum use the item's own unit
      setAvailableUnits([itemUnit]);
    }
  };

  // Handle QR code dialog
  const handleQrCode = (item) => {
    setQrItem(item);
    setQrDialogOpen(true);
  };

  // Handle opening details dialog
  const handleOpenDetails = (item) => {
    setDetailsItem(item);
    setDetailsDialogOpen(true);
  };

  // Handle closing details dialog
  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
  };

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get unique categories for filter dropdown
  const uniqueCategories = [
    "all",
    ...new Set(items.map((item) => item.category)),
  ];

  // Handle sending email notifications for low stock items
  const handleSendNotifications = async () => {
    try {
      // In a real app, this would connect to an API endpoint to send emails
      // Here we'll simulate the API call

      // Prepare notification data based on settings
      const notificationData = {
        items: lowStockItems,
        criticalItems: lowStockItems.filter(
          (item) => getLowStockSeverity(item) === "critical"
        ),
        warningItems: lowStockItems.filter(
          (item) => getLowStockSeverity(item) === "warning"
        ),
        recipients: emailRecipients,
        frequency: notificationSettings.emailFrequency,
        timestamp: new Date(),
      };

      // In a real implementation, we would send this to the backend
      console.log("Sending notifications:", notificationData);

      setSnackbar({
        open: true,
        message: `Low stock notifications sent to ${emailRecipients.length} recipient(s)`,
        severity: "success",
      });

      // Add to alert history
      addToAlertHistory();
    } catch (err) {
      console.error("Error sending notifications:", err);
      setSnackbar({
        open: true,
        message: "Failed to send notifications. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle notification settings changes
  const handleNotificationSettingChange = (setting, value) => {
    setNotificationSettings({
      ...notificationSettings,
      [setting]: value,
    });
  };

  // Toggle expanded section
  const toggleExpandedSection = () => {
    setNotificationSettings({
      ...notificationSettings,
      expandedSection: !notificationSettings.expandedSection,
    });
  };

  // Open low stock alerts dialog
  const handleOpenAlertsDialog = () => {
    setAlertsDialogOpen(true);
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // Check if an item is low in stock
  const isLowStock = (item) => {
    if (notificationSettings.notificationThreshold === "percentage") {
      // Calculate percentage-based threshold
      const percentThreshold =
        (notificationSettings.percentageThreshold / 100) *
        (item.initialQuantity || 20);
      return item.quantity <= percentThreshold;
    } else {
      // Use custom threshold for each item
      // If units are different, convert the threshold to the item's unit
      if (
        item.minThresholdUnit &&
        item.unit &&
        item.minThresholdUnit !== item.unit
      ) {
        try {
          // Convert threshold to the same unit as item quantity for accurate comparison
          const convertedThreshold = convertUnits(
            item.minThreshold || 0,
            item.minThresholdUnit,
            item.unit
          );

          // Debug logging to help diagnose unit conversion issues
          console.log(
            `Unit conversion for ${item.name}: ${item.minThreshold} ${item.minThresholdUnit} = ${convertedThreshold} ${item.unit}. Current quantity: ${item.quantity} ${item.unit}`
          );

          // Ensure we're making a numerical comparison with proper conversion
          if (convertedThreshold !== null && !isNaN(convertedThreshold)) {
            return parseFloat(item.quantity) <= parseFloat(convertedThreshold);
          } else {
            console.warn(
              `Invalid conversion result for ${item.name}: ${convertedThreshold}`
            );
            // Fallback to direct comparison if conversion result is invalid
            return item.quantity <= item.minThreshold;
          }
        } catch (error) {
          console.warn(
            `Could not convert units for comparison: ${item.minThresholdUnit} to ${item.unit}`,
            error
          );
          // Fallback to direct comparison if conversion fails
          return item.quantity <= item.minThreshold;
        }
      } else {
        // Units are the same or not specified, direct comparison
        return item.quantity <= item.minThreshold;
      }
    }
  };

  // Calculate percentage of stock remaining for visual indicators
  const calculateStockPercentage = (item) => {
    // Base threshold (use defined threshold or 0)
    const threshold = item.minThreshold ?? 0;
    let effectiveThreshold = threshold;

    // Convert threshold to item unit if units differ
    if (
      item.minThresholdUnit &&
      item.unit &&
      item.minThresholdUnit !== item.unit
    ) {
      try {
        const converted = convertUnits(
          threshold,
          item.minThresholdUnit,
          item.unit
        );
        if (converted != null && !isNaN(converted)) {
          effectiveThreshold = converted;
        }
      } catch (err) {
        console.warn(
          `Unit conversion error in calculateStockPercentage: ${err.message}`
        );
      }
    }

    // Determine max expected for percentage calculation
    const maxExpected = item.initialQuantity ?? effectiveThreshold * 3;
    if (!maxExpected || maxExpected <= 0) {
      return 0;
    }
    const percentage = (item.quantity / maxExpected) * 100;
    return Math.min(100, Math.max(0, percentage));
  };

  // Calculate stock status for visual indicator
  const getStockStatus = (item) => {
    if (
      !item ||
      typeof item.quantity !== "number" ||
      typeof item.minThreshold !== "number"
    ) {
      return "unknown";
    }

    if (item.quantity <= 0) {
      return "outOfStock";
    }

    // Use the same logic as getLowStockSeverity for consistency
    const severity = getLowStockSeverity(item);
    if (severity === "critical") {
      return "critical";
    } else if (severity === "warning") {
      return "warning";
    } else {
      return "ok";
    }
  };

  // Get stock status color for visual indicators
  const getStockStatusColor = (status) => {
    switch (status) {
      case "outOfStock":
        return "error.main";
      case "critical":
        return "error.light";
      case "warning":
        return "warning.main";
      case "ok":
        return "success.main";
      default:
        return "text.disabled";
    }
  };

  // Format any dates in inventory item
  const formatDate = (date) => {
    if (!date) return "N/A";

    try {
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  // Handle unit conversion between quantity and amount
  const handleUnitConversion = (sourceField) => {
    if (!useItem || !useItem.unit) return;

    try {
      // If both units are set and we have a value to convert
      if (sourceField === "quantity" && useQuantity && useUnit) {
        // When user changes the quantity or unit, update the conversion preview
        const quantity = parseFloat(useQuantity);

        // No conversion needed if units are the same
        if (useUnit === useItem.unit) {
          setIsConversionValid(true);
          setConversionPreview(null);
          return;
        }

        try {
          // Use mathjs to convert between units
          const convertedQuantity = convertUnits(
            quantity,
            useUnit,
            useItem.unit
          );

          if (convertedQuantity !== null) {
            setIsConversionValid(true);
            setConversionPreview(
              `${quantity} ${useUnit} = ${convertedQuantity.toFixed(3)} ${
                useItem.unit
              }`
            );

            // Update amount field for consistency
            setUseAmount(convertedQuantity.toFixed(3));
          } else {
            setIsConversionValid(false);
            setConversionPreview(
              `Cannot convert from ${useUnit} to ${useItem.unit}`
            );
          }
        } catch (error) {
          console.error("Unit conversion error:", error);
          setIsConversionValid(false);
          setConversionPreview(
            `Error: ${error.message || "Invalid conversion"}`
          );
        }
      }
    } catch (error) {
      console.error("Error in unit conversion handler:", error);
      setIsConversionValid(false);
      setConversionPreview("Error processing unit conversion");
    }
  };

  // Handle submitting the use dialog (deducting inventory)
  const handleUseSubmit = async () => {
    if (!useItem || !useQuantity || !useUnit || !useReason) {
      setUseError("Please enter a valid amount and reason.");
      return;
    }
    // Convert the entered quantity to the item's base unit
    let deduction = null;
    try {
      deduction = convertUnits(parseFloat(useQuantity), useUnit, useItem.unit);

      // Format for better readability with consistent decimal places
      const formattedDeduction = Number(deduction).toFixed(6);
      console.log(
        `Converting ${useQuantity} ${useUnit} to ${formattedDeduction} ${useItem.unit}`
      );
    } catch (e) {
      console.error("Unit conversion error:", e);
      setUseError("Unit conversion failed. Please check units.");
      return;
    }

    if (deduction === null || isNaN(deduction) || deduction <= 0) {
      setUseError("Invalid or incompatible units for deduction.");
      return;
    }

    if (deduction > useItem.quantity) {
      // Calculate how much more is needed in the user's selected unit
      let availableInSelectedUnit;
      try {
        availableInSelectedUnit = convertUnits(
          useItem.quantity,
          useItem.unit,
          useUnit
        );
        setUseError(
          `Insufficient stock. Available: ${Number(useItem.quantity).toFixed(
            4
          )} ${useItem.unit} (${Number(availableInSelectedUnit).toFixed(
            4
          )} ${useUnit})`
        );
      } catch (e) {
        setUseError(
          `Cannot use more than available: ${useItem.quantity} ${useItem.unit}`
        );
      }
      return;
    }
    setUseError(null);
    try {
      // Get the item ID more safely, checking multiple possible properties
      const itemId = useItem._id || useItem.id || useItem.itemId;

      // Add debug logging to identify the issue
      console.log("[Inventory] Item details for update:", {
        itemName: useItem.name,
        itemId,
        originalItemObject: useItem,
      });

      // Check if we have a valid ID
      if (!itemId) {
        setUseError(
          "Item ID is missing. Please try again or refresh the page."
        );
        console.error(
          "[Inventory] Cannot update quantity - missing item ID:",
          useItem
        );
        return;
      }

      await inventoryAPI.updateQuantity(itemId, {
        quantity: deduction,
        action: "subtract",
        reason: useReason,
      });

      setSnackbar({
        open: true,
        message: `Used ${useQuantity} ${useUnit} (${deduction} ${useItem.unit}) from ${useItem.name}`,
        severity: "success",
      });
      setUseDialogOpen(false);
      fetchInventory();
    } catch (err) {
      console.error("[Inventory] Error updating quantity:", err);
      setUseError(
        err.response?.data?.message ||
          "Failed to update inventory. Please try again."
      );
    }
  };

  // Handle exporting usage logs
  const handleExportLogs = () => {
    try {
      // Create CSV header
      const csvHeader = [
        "Date",
        "Item Name",
        "Action",
        "Quantity",
        "User",
        "Notes",
      ];

      // Format logs as CSV rows
      const csvRows = usageLogs.map((log) => [
        log.formattedDate,
        log.itemName || "Unknown Item",
        log.actionDisplay || log.action,
        `${log.quantity} ${log.itemUnit || ""}`,
        log.userName || "Unknown User",
        log.notes || "",
      ]);

      // Combine header and rows
      const csvContent = [
        csvHeader.join(","),
        ...csvRows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      // Create download link
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `inventory-usage-logs-${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: "Usage logs exported as CSV",
        severity: "success",
      });
    } catch (err) {
      console.error("Error exporting usage logs:", err);
      setSnackbar({
        open: true,
        message: "Failed to export usage logs. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle resetting the usage log filters
  const handleResetUsageLogFilters = () => {
    setUsageLogsFilters({
      startDate: "",
      endDate: "",
      action: "",
      itemId: "",
      userId: "",
      itemName: "",
    });
    // Fetch with reset filters after a short delay to ensure state updates
    setTimeout(() => fetchUsageLogs(1), 100);
  };

  // Fetch all users when component mounts for usage log name resolution
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await userAPI.getAllUsers();
        setUsers(response.data);
      } catch (err) {
        console.error("Error fetching users:", err);
        setUsers([]); // fallback to empty array
      }
    };
    fetchAllUsers();
  }, []);

  return (
    <Layout>
      {/* Main content area */}
      <Box sx={{ py: 2, px: { xs: 2, md: 3 } }}>
        {/* Tab controls */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            aria-label="inventory management tabs"
          >
            <Tab label="All Items" />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <span>Low Stock</span>
                  {lowStockItems.length > 0 && (
                    <Chip
                      size="small"
                      label={lowStockItems.length}
                      color="error"
                      sx={{ ml: 1, height: 20 }}
                    />
                  )}
                </Box>
              }
            />
            <Tab label="Usage Logs" />
          </Tabs>
        </Box>

        {/* Filters and search */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name, category, or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="category-filter-label">Category</InputLabel>
                <Select
                  labelId="category-filter-label"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {uniqueCategories
                    .filter((c) => c !== "all")
                    .map((category) => (
                      <MenuItem key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="bookable-filter-label">
                  Bookable Status
                </InputLabel>
                <Select
                  labelId="bookable-filter-label"
                  value={filterBookable}
                  onChange={(e) => setFilterBookable(e.target.value)}
                  label="Bookable Status"
                >
                  <MenuItem value="all">All Items</MenuItem>
                  <MenuItem value="bookable">Bookable Items</MenuItem>
                  <MenuItem value="nonbookable">Non-Bookable Items</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchInventory}
                  size="small"
                >
                  Refresh
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Tab content */}
        <Box role="tabpanel" hidden={activeTab !== 0}>
          {activeTab === 0 && (
            <>
              {/* Low stock alert banner - now displayed permanently at the top */}
              {lowStockItems.length > 0 && (
                <Alert
                  severity={
                    lowStockItems.some(
                      (item) => getLowStockSeverity(item) === "critical"
                    )
                      ? "error"
                      : "warning"
                  }
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setActiveTab(1)}
                    >
                      View All
                    </Button>
                  }
                >
                  {lowStockItems.length} items are low in stock
                  {lowStockItems.filter(
                    (item) => getLowStockSeverity(item) === "critical"
                  ).length > 0 &&
                    ` (${
                      lowStockItems.filter(
                        (item) => getLowStockSeverity(item) === "critical"
                      ).length
                    } critical)`}{" "}
                  and need attention!
                </Alert>
              )}

              {/* Actions bar */}
              <Box
                sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog("add")}
                >
                  Add Item
                </Button>
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<ImportIcon />}
                    onClick={() => setImportDialogOpen(true)}
                    sx={{ mr: 1 }}
                  >
                    Import
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ExportIcon />}
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                </Box>
              </Box>

              {/* Loading state */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : filteredItems.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No inventory items found. Add a new item to get started!
                </Alert>
              ) : (
                <>
                  {/* Enhanced Inventory Table */}
                  <InventoryTable
                    items={filteredItems}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleOpenDetails={handleOpenDetails}
                    handleQrCode={handleQrCode}
                    handleOpenDialog={handleOpenDialog}
                    handleDelete={handleDelete}
                    handleOpenUseDialog={handleOpenUseDialog}
                    getStockStatus={getStockStatus}
                    getStockStatusColor={getStockStatusColor}
                    calculateStockPercentage={calculateStockPercentage}
                  />

                  {/* Pagination */}
                  <TablePagination
                    component="div"
                    count={filteredItems.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                  />
                </>
              )}
            </>
          )}
        </Box>

        {/* Low Stock Tab content */}
        <Box role="tabpanel" hidden={activeTab !== 1}>
          {activeTab === 1 && (
            <>
              {/* Low stock actions bar */}
              <Box
                sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}
              >
                <Typography variant="h6">
                  {lowStockItems.length} Items Below Threshold
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<NotificationsIcon />}
                  onClick={handleOpenAlertsDialog}
                >
                  Manage Alerts
                </Button>
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : lowStockItems.length === 0 ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  No items are below their minimum threshold. Stock levels are
                  good!
                </Alert>
              ) : (
                <>
                  {/* Enhanced Inventory Table for low stock items */}
                  <InventoryTable
                    items={lowStockItems}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleOpenDetails={handleOpenDetails}
                    handleQrCode={handleQrCode}
                    handleOpenDialog={handleOpenDialog}
                    handleDelete={handleDelete}
                    handleOpenUseDialog={(item) => {
                      setUseItem(item);
                      setUseDialogOpen(true);
                    }}
                    getStockStatus={getStockStatus}
                    getStockStatusColor={getStockStatusColor}
                    calculateStockPercentage={calculateStockPercentage}
                  />

                  {/* Pagination */}
                  <TablePagination
                    component="div"
                    count={lowStockItems.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                  />
                </>
              )}
            </>
          )}
        </Box>

        {/* Usage Logs Tab content */}
        <Box role="tabpanel" hidden={activeTab !== 2}>
          {activeTab === 2 && (
            <>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Usage Log Filters</Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={handleResetUsageLogFilters}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      Reset Filters
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      onClick={handleExportLogs}
                      size="small"
                      disabled={usageLogs.length === 0}
                    >
                      Export Logs
                    </Button>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start Date"
                        value={
                          usageLogsFilters.startDate
                            ? dayjs(usageLogsFilters.startDate)
                            : null
                        }
                        onChange={(newValue) => {
                          setUsageLogsFilters((prev) => ({
                            ...prev,
                            startDate: newValue
                              ? newValue.format("YYYY-MM-DD")
                              : "",
                          }));
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            variant: "outlined",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="End Date"
                        value={
                          usageLogsFilters.endDate
                            ? dayjs(usageLogsFilters.endDate)
                            : null
                        }
                        onChange={(newValue) => {
                          setUsageLogsFilters((prev) => ({
                            ...prev,
                            endDate: newValue
                              ? newValue.format("YYYY-MM-DD")
                              : "",
                          }));
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            variant: "outlined",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="action-filter-label">Action</InputLabel>
                      <Select
                        labelId="action-filter-label"
                        value={usageLogsFilters.action}
                        onChange={(e) => {
                          setUsageLogsFilters((prev) => ({
                            ...prev,
                            action: e.target.value,
                          }));
                        }}
                        label="Action"
                      >
                        <MenuItem value="">All Actions</MenuItem>
                        <MenuItem value="add">Added</MenuItem>
                        <MenuItem value="subtract">Removed</MenuItem>
                        <MenuItem value="adjust">Adjusted</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="item-filter-label">Item</InputLabel>
                      <Select
                        labelId="item-filter-label"
                        value={usageLogsFilters.itemId || ""}
                        onChange={(e) => {
                          setUsageLogsFilters((prev) => ({
                            ...prev,
                            itemId: e.target.value,
                          }));
                        }}
                        label="Item"
                      >
                        <MenuItem value="">All Items</MenuItem>
                        {items.map((item) => (
                          <MenuItem
                            key={item._id || item.id}
                            value={item._id || item.id}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => fetchUsageLogs(1)}
                    >
                      Apply Filters
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {usageLogsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : usageLogs.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No usage logs found with the current filters.
                </Alert>
              ) : (
                <>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Item</TableCell>
                          <TableCell>Action</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell>User</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usageLogs.map((log) => (
                          <TableRow key={log.id} hover>
                            <TableCell>{log.formattedDate}</TableCell>
                            <TableCell>
                              <Tooltip title={`ID: ${log.inventoryId}`}>
                                <Typography variant="body2">
                                  {(() => {
                                    // Normalize inventoryId (handle object or string)
                                    const id =
                                      typeof log.inventoryId === "object"
                                        ? log.inventoryId._id ||
                                          log.inventoryId.id
                                        : log.inventoryId;
                                    // Find the item in the items array
                                    const found = items.find(
                                      (i) => i._id === id || i.id === id
                                    );
                                    return found?.name || "Unknown Item";
                                  })()}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={log.actionDisplay}
                                size="small"
                                color={
                                  log.action === "add"
                                    ? "success"
                                    : log.action === "subtract"
                                    ? "error"
                                    : "default"
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              {log.displayQuantity ||
                                `${log.quantity} ${log.itemUnit}`.trim()}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                // Normalize userId (handle object or string)
                                const id =
                                  typeof log.userId === "object"
                                    ? log.userId._id || log.userId.id
                                    : log.userId;
                                // Find the user in the users array
                                const found =
                                  users &&
                                  users.find(
                                    (u) => u._id === id || u.id === id
                                  );
                                return (
                                  found?.displayName ||
                                  found?.name ||
                                  (found?.email
                                    ? found.email.split("@")[0]
                                    : null) ||
                                  log.userDisplayName ||
                                  log.userName ||
                                  (typeof log.userId === "string" &&
                                  log.userId.includes("@")
                                    ? log.userId.split("@")[0]
                                    : log.userId || "System")
                                );
                              })()}
                            </TableCell>
                            <TableCell>{log.notes || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                  >
                    <Button
                      disabled={usageLogsPagination.page <= 1}
                      onClick={() =>
                        fetchUsageLogs(usageLogsPagination.page - 1)
                      }
                    >
                      Previous
                    </Button>
                    <Typography sx={{ mx: 2, alignSelf: "center" }}>
                      Page {usageLogsPagination.page} of{" "}
                      {usageLogsPagination.pages || 1}
                    </Typography>
                    <Button
                      disabled={
                        usageLogsPagination.page >= usageLogsPagination.pages
                      }
                      onClick={() =>
                        fetchUsageLogs(usageLogsPagination.page + 1)
                      }
                    >
                      Next
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Item Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        {detailsItem && (
          <>
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6">{detailsItem.name}</Typography>
                <Chip
                  label={detailsItem.category}
                  color={
                    detailsItem.category === "instrument"
                      ? "primary"
                      : detailsItem.category === "glassware"
                      ? "secondary"
                      : detailsItem.category === "component"
                      ? "success"
                      : "default"
                  }
                  size="small"
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {/* Basic Information Section */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Basic Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Department
                          </Typography>
                          <Typography variant="body1">
                            {detailsItem.department || "Not specified"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Location
                          </Typography>
                          <Typography variant="body1">
                            {detailsItem.location || "Not specified"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Description
                          </Typography>
                          <Typography variant="body1">
                            {detailsItem.description ||
                              "No description provided"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Inventory Status Section */}
                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      borderColor: getStockStatusColor(
                        getStockStatus(detailsItem)
                      ),
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        bgcolor: getStockStatusColor(
                          getStockStatus(detailsItem)
                        ),
                      }}
                    />
                    <Typography variant="subtitle1" gutterBottom>
                      Inventory Status
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-end", mb: 2 }}
                    >
                      <Typography variant="h3" component="div" sx={{ mr: 1 }}>
                        {detailsItem.quantity}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {detailsItem.unit}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={calculateStockPercentage(detailsItem)}
                      color={
                        getStockStatus(detailsItem) === "outOfStock"
                          ? "error"
                          : getStockStatus(detailsItem) === "critical"
                          ? "error"
                          : getStockStatus(detailsItem) === "warning"
                          ? "warning"
                          : "success"
                      }
                      sx={{ height: 8, borderRadius: 1, mb: 1 }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Minimum Threshold: {detailsItem.minThreshold}{" "}
                        {detailsItem.minThresholdUnit || detailsItem.unit}
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          getStockStatus(detailsItem) === "outOfStock"
                            ? "Out of Stock"
                            : getStockStatus(detailsItem) === "critical"
                            ? "Critical"
                            : getStockStatus(detailsItem) === "warning"
                            ? "Low Stock"
                            : "In Stock"
                        }
                        color={
                          getStockStatus(detailsItem) === "outOfStock"
                            ? "error"
                            : getStockStatus(detailsItem) === "critical"
                            ? "error"
                            : getStockStatus(detailsItem) === "warning"
                            ? "warning"
                            : "success"
                        }
                      />
                    </Box>
                  </Paper>
                </Grid>

                {/* Item Properties Section */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Item Properties
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Bookable
                        </Typography>
                        <Typography variant="body1">
                          {detailsItem.isBookable ? "Yes" : "No"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Created Date
                        </Typography>
                        <Typography variant="body1">
                          {detailsItem.createdAt
                            ? formatDate(detailsItem.createdAt)
                            : "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Last Updated
                        </Typography>
                        <Typography variant="body1">
                          {detailsItem.updatedAt
                            ? formatDate(detailsItem.updatedAt)
                            : "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          ID
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ wordBreak: "break-all" }}
                        >
                          {detailsItem._id || "N/A"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Additional Properties (Custom Fields) */}
                {detailsItem.customFields &&
                  Object.keys(detailsItem.customFields).length > 0 && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Additional Properties
                        </Typography>
                        <Grid container spacing={2}>
                          {Object.entries(detailsItem.customFields).map(
                            ([key, value]) => (
                              <Grid item xs={6} key={key}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {key}
                                </Typography>
                                <Typography variant="body1">
                                  {value.toString()}
                                </Typography>
                              </Grid>
                            )
                          )}
                        </Grid>
                      </Paper>
                    </Grid>
                  )}

                {/* Usage and History Section Placeholder */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Recent Activity
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      View the usage logs tab for detailed usage history.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
              <Button
                onClick={() => handleOpenUseDialog(detailsItem)}
                variant="contained"
                color="primary"
                startIcon={<InventoryIcon />}
              >
                Use Item
              </Button>
              <Box>
                <Button onClick={handleCloseDetails}>Close</Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    handleCloseDetails();
                    handleOpenDialog("edit", detailsItem);
                  }}
                  sx={{ ml: 1 }}
                  startIcon={<EditIcon />}
                >
                  Edit
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Add/Edit Item Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === "add" ? "Add New Item" : "Edit Item"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                margin="dense"
                name="name"
                label="Item Name"
                type="text"
                fullWidth
                required
                value={currentItem.name}
                onChange={handleInputChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense" required>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  name="category"
                  value={currentItem.category}
                  onChange={handleInputChange}
                  label="Category"
                >
                  {departmentCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="quantity"
                label="Quantity"
                type="number"
                fullWidth
                required
                value={currentItem.quantity}
                onChange={handleInputChange}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {currentItem.unit}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense" required>
                <InputLabel id="unit-label">Unit</InputLabel>
                <Select
                  labelId="unit-label"
                  name="unit"
                  value={currentItem.unit}
                  onChange={(e) => {
                    handleInputChange(e);
                    // Update compatible units for threshold when main unit changes
                    const compatible = getCompatibleUnits(e.target.value);
                    setAvailableUnits(
                      compatible.length > 0 ? compatible : [e.target.value]
                    );
                    setMinThresholdUnit(e.target.value); // Default threshold unit to main unit
                    setCurrentItem((prev) => ({
                      ...prev,
                      minThresholdUnit: e.target.value,
                    }));
                  }}
                  label="Unit"
                >
                  {/* For Add mode, show all departmentUnits. For Edit mode, show compatible units */}
                  {dialogMode === "add"
                    ? (departmentUnits.length > 0
                        ? departmentUnits
                        : defaultUnits
                      ).map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      ))
                    : getCompatibleUnits(
                        currentItem.unit || departmentUnits[0] || "pcs"
                      ).map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="minThreshold"
                label="Minimum Threshold"
                type="number"
                fullWidth
                value={currentItem.minThreshold}
                onChange={handleInputChange}
                variant="outlined"
                helperText="Alert will be triggered when quantity drops below this threshold"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {minThresholdUnit}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="threshold-unit-label">
                  Threshold Unit
                </InputLabel>
                <Select
                  labelId="threshold-unit-label"
                  value={minThresholdUnit}
                  onChange={handleThresholdUnitChange}
                  label="Threshold Unit"
                >
                  {getCompatibleUnits(
                    currentItem.unit || departmentUnits[0] || "pcs"
                  ).map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Unit for the minimum threshold (defaults to item unit)
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="location"
                label="Location"
                type="text"
                fullWidth
                value={currentItem.location}
                onChange={handleInputChange}
                variant="outlined"
                helperText="e.g., Room 101, Cabinet 3, Shelf 2"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="description"
                label="Description"
                type="text"
                fullWidth
                multiline
                rows={3}
                value={currentItem.description}
                onChange={handleInputChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentItem.isBookable}
                    onChange={(e) =>
                      setCurrentItem({
                        ...currentItem,
                        isBookable: e.target.checked,
                      })
                    }
                    name="isBookable"
                    color="primary"
                  />
                }
                label="This item can be booked/reserved"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {dialogMode === "add" ? "Add Item" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Inventory Items</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                label="Import Data (JSON)"
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                helperText="Paste JSON data here"
              />
            </Grid>
            <Grid item xs={12}>
              {importError && (
                <Alert severity="error" onClose={() => setImportError(null)}>
                  {importError}
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={!importData}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>QR Code for {qrItem?.name}</DialogTitle>
        <DialogContent sx={{ display: "flex", justifyContent: "center" }}>
          {qrItem && <QRCode value={JSON.stringify(qrItem)} size={256} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Use Item Dialog */}
      <Dialog
        open={useDialogOpen}
        onClose={() => setUseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Use Item: {useItem?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Current stock: {useItem?.quantity} {useItem?.unit}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Quantity to use"
                  type="number"
                  value={useQuantity}
                  onChange={(e) => {
                    setUseQuantity(e.target.value);
                    // Recalculate conversion when quantity changes
                    if (e.target.value && useUnit !== useItem?.unit) {
                      handleUnitConversion("quantity");
                    }
                  }}
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Select
                          value={useUnit}
                          onChange={(e) => {
                            setUseUnit(e.target.value);
                            // Recalculate conversion when unit changes
                            if (useQuantity) {
                              handleUnitConversion("quantity");
                            }
                          }}
                          variant="standard"
                          sx={{ minWidth: 60 }}
                        >
                          {/* Debug output to console to verify availableUnits */}
                          {console.log(
                            "Rendering unit dropdown with units:",
                            availableUnits
                          )}

                          {/* Always render all available units, forcing display of compatible units */}
                          {availableUnits && availableUnits.length > 0 ? (
                            availableUnits.map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {unit}
                              </MenuItem>
                            ))
                          ) : (
                            // Fallback if no compatible units
                            <MenuItem value={useItem?.unit || "pcs"}>
                              {useItem?.unit || "pcs"}
                            </MenuItem>
                          )}
                        </Select>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {useQuantity && useItem && useUnit !== useItem.unit && (
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color={isConversionValid ? "text.secondary" : "error"}
                    sx={{ fontStyle: "italic" }}
                  >
                    {conversionPreview}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason for usage"
                  value={useReason}
                  onChange={(e) => setUseReason(e.target.value)}
                  margin="normal"
                  required
                  placeholder="Enter reason for usage"
                  multiline
                  rows={2}
                />
              </Grid>

              {useError && (
                <Grid item xs={12}>
                  <Alert severity="error" onClose={() => setUseError(null)}>
                    {useError}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUseDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUseSubmit}
            disabled={!useQuantity || !useReason || !isConversionValid}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* System Notifications */}
      <SystemNotifications
        open={notificationDrawerOpen}
        onClose={() => setNotificationDrawerOpen(false)}
        fetchUnreadCount={fetchUnreadNotificationsCount}
      />
    </Layout>
  );
};

export default Inventory;
